import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "../../lib/utils";

const R = 21;
const C = 2 * Math.PI * R;

/** Floating back-to-top control with a circular scroll-progress trace. */
export function ScrollProgressTop() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frame = 0;
    const read = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const y = window.scrollY;
      setProgress(max > 8 ? Math.min(1, Math.max(0, y / max)) : 0);
      setVisible(y > window.innerHeight * 0.6);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const complete = progress > 0.995;

  return (
    <button
      type="button"
      onClick={() =>
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "auto"
            : "smooth",
        })
      }
      aria-label={`Back to top — ${Math.round(progress * 100)}% of the page viewed`}
      tabIndex={visible ? 0 : -1}
      aria-hidden={!visible}
      className={cn(
        "group fixed bottom-6 right-5 z-50 grid size-[52px] place-items-center rounded-full",
        "bg-background/55 backdrop-blur-md transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] sm:bottom-8 sm:right-8",
        visible
          ? "pointer-events-auto translate-y-0 opacity-100"
          : "pointer-events-none translate-y-4 opacity-0",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute inset-0 rounded-full bg-primary/25 blur-md transition-opacity duration-700",
          complete ? "opacity-100" : "opacity-0 group-hover:opacity-60",
        )}
      />
      <svg
        aria-hidden="true"
        viewBox="0 0 52 52"
        className="absolute inset-0 size-full -rotate-90"
        fill="none"
      >
        <circle
          cx="26"
          cy="26"
          r={R}
          stroke="color-mix(in oklab, var(--foreground) 16%, transparent)"
          strokeWidth="1"
        />
        <circle
          cx="26"
          cy="26"
          r={R}
          stroke="var(--primary)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C * (1 - progress)}
          className="transition-[stroke-dashoffset] duration-200 ease-out"
        />
      </svg>
      <ArrowUp className="relative size-4 text-foreground/80 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-0.5 group-hover:text-foreground" />
    </button>
  );
}